import { useState, useCallback, useEffect } from "react";
import { isAxiosError } from "axios";
import { useUpsertFieldValue } from "./useUpsertFieldValue";
import { useDeleteFieldValue } from "./useDeleteFieldValue";
import { FieldValue } from "./useGetFieldValues";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { getOptionCodes, getOptionValues } from "src/utils/dataListOptions.ts";
import { extractValidationMessages } from "src/utils/validation";
import { ValidationMessage } from "src/components/QuestionnaireForm/FormProperty/validationTypes";
import { DataListOption } from "src/api/sure-api/dataList/useGetAllDataList";

type Options = {
    actionPlanId: string;
    actionPlanActionId: string;
    fieldValue: FieldValue | undefined;
    actionFieldId: number;
    position: number;
    onSuccess?: () => void;
    onError?: (error: any) => void;
    onValidationError?: (messages: ValidationMessage[]) => void;
};

export const useActionFieldInput = (options: Options) => {
    const { actionPlanId, actionPlanActionId, fieldValue, onSuccess, onError, onValidationError } = options;

    const { setIsFieldValueUpdating } = useActionsContext();

    // Update field value mutation
    const mutateFieldValueParams = {
        actionPlanId,
        actionPlanActionId,
        fieldValueId: fieldValue?.id ?? null,
        onSuccess: () => {
            setIsSaving(false);
            setIsFieldValueUpdating(false);
            onSuccess?.();
        },
        onError: (error: any) => {
            setIsSaving(false);
            setIsFieldValueUpdating(false);

            if (isAxiosError(error)) {
                const statusCode = error.response?.status;
                if (statusCode === 422) {
                    const violationMessages = extractValidationMessages(error.response?.data);
                    if (violationMessages.length > 0) {
                        onValidationError?.(violationMessages);
                        return;
                    }
                }
            }

            onError?.(error);
        },
    };

    const updateFieldValueMutation = useUpsertFieldValue(mutateFieldValueParams);
    const deleteFieldValueMutation = useDeleteFieldValue(mutateFieldValueParams);

    // Local state for input value
    const [inputValue, setInputValue] = useState<string>("");
    const [dataListOptionCodes, setDataListOptionCodes] = useState<Array<string> | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Initialize input value from existing field value
    useEffect(() => {
        if (fieldValue?.value !== undefined) {
            setInputValue(fieldValue.value);
        }
        if (fieldValue?.dataListOptionCodes !== undefined) {
            setDataListOptionCodes(
                Array.isArray(fieldValue.dataListOptionCodes) ? fieldValue.dataListOptionCodes : null,
            );
        }
    }, [fieldValue]);

    // Handle input change
    const handleInputChange = useCallback((value: string) => {
        setInputValue(value);
    }, []);

    const handleDataListChange = useCallback((value: DataListOption[] | DataListOption | null) => {
        if (value === null) {
            setInputValue("");
            setIsSaving(true);
            setIsFieldValueUpdating(true);
            setDataListOptionCodes(null);

            updateFieldValueMutation.mutate({
                actionFieldId: options.actionFieldId,
                value: "",
                dataListOptionCodes: null,
                position: options.position,
                actionPlanAction: `/api/action_plans/${actionPlanId}/action/${actionPlanActionId}`,
            });
        } else {
            const dataListOptions = Array.isArray(value) ? value : [value];
            const mappedValue = getOptionValues(dataListOptions);
            const mappedCodes = getOptionCodes(dataListOptions);

            setInputValue(mappedValue);
            setIsSaving(true);
            setIsFieldValueUpdating(true);
            setDataListOptionCodes(mappedCodes);

            updateFieldValueMutation.mutate({
                actionFieldId: options.actionFieldId,
                value: mappedValue,
                dataListOptionCodes: mappedCodes,
                position: options.position,
                actionPlanAction: `/api/action_plans/${actionPlanId}/action/${actionPlanActionId}`,
            });
        }
    }, []);

    // Handle input blur (immediate save)
    const handleInputBlur = useCallback(() => {
        const trimmedValue = inputValue.trim();

        if (trimmedValue === "" && fieldValue === undefined) return;

        if (trimmedValue !== fieldValue?.value) {
            setIsSaving(true);
            setIsFieldValueUpdating(true);

            if (trimmedValue !== "") {
                updateFieldValueMutation.mutate({
                    actionFieldId: options.actionFieldId,
                    value: trimmedValue,
                    position: options.position,
                    actionPlanAction: `/api/action_plans/${actionPlanId}/action/${actionPlanActionId}`,
                });

                return;
            }

            deleteFieldValueMutation.mutate();
        }
    }, [inputValue, fieldValue?.value, fieldValue, updateFieldValueMutation]);

    return {
        // Current input value
        value: inputValue,
        dataListOptionCodes: dataListOptionCodes ?? null,

        // Input handlers
        onChange: handleInputChange,
        onDataListChange: handleDataListChange,
        onBlur: handleInputBlur,

        // State indicators
        isSaving,
        hasError: updateFieldValueMutation.isError,
        error: updateFieldValueMutation.error,

        // Field value data
        fieldValue,

        // Manual save function
        save: () => handleInputBlur(),

        // Reset to original value
        reset: () => {
            if (fieldValue?.value !== undefined) {
                setInputValue(fieldValue.value);
            }
        },
    };
};
